// Access types (replaces UserRole)
export interface UserAccess {
  hasPortalAccess: boolean
  hasBackofficeAccess: boolean
}

export interface User {
  id: string
  name: string | null
  email: string
  emailVerified: Date | null
  image: string | null
  hasPortalAccess: boolean
  hasBackofficeAccess: boolean
  createdAt: Date
  updatedAt: Date
}

export type ModuleType = 'MEDEWERKERS' | 'CRM' | 'PLANNING' | 'ASSETS' | 'BRUIKLEEN' | 'VERLOF' | 'VERZUIM' | 'REISAFSTANDEN' | 'UREN' | 'DECLARATIES' | 'DOCUMENTEN' | 'NOTIFICATIES' | 'PAYROLL' | 'ENTITEITEN' | 'OFFERTES' | 'WERKBONNEN' | 'FACTUREN' | 'SELFSERVICE' | 'KALENDER' | 'COMMUNICATIE' | 'ONBOARDING' | 'INTERCOMPANY' | 'TAKEN' | 'IMPERSONATION' | 'FORMULIEREN' | 'INKOOP' | 'KWALITEIT' | 'PROJECTEN' | 'POSTVAK_IN' | 'VOORRAAD' | 'OPLEIDINGEN' | 'RECRUITMENT'

export interface ModulePermission {
  module: ModuleType
  canView: boolean
  canEdit: boolean
  canDelete: boolean
}

// Employee types
export type EmployeeStatus =
  | 'CONCEPT'
  | 'ONBOARDING'
  | 'ACTIEF'
  | 'INACTIEF'
  | 'ZIEK'
  | 'VERLOF'
  | 'UITGEDIENST'
export type ContractType = 'BEPAALDE_TIJD' | 'ONBEPAALDE_TIJD' | 'OPROEP' | 'STAGE' | 'PAYROLL'

export interface EmployeeComputed {
  fte: number
  vacationDaysThisYear: number
  atvDaysThisYear: number
  contractEndDate: string | null
}

// Certificate types
export type CertificateStatus = 'ACTIEF' | 'BIJNA_VERLOPEN' | 'VERLOPEN' | 'ONTBREEKT'

// API response types
export interface ApiResponse<T = unknown> {
  data: T
  meta?: {
    total?: number
    page?: number
    limit?: number
  }
}

export interface ApiError {
  error: {
    code: string
    message: string
    details?: Record<string, unknown>
  }
}

// Pagination
export interface PaginationParams {
  page?: number
  limit?: number
}

// Common utility types
export type Nullable<T> = T | null
export type Optional<T> = T | undefined
export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}

// CRM types
export type ClientType = 'KLANT' | 'PROSPECT' | 'LEVERANCIER' | 'PARTNER' | 'INTERN'
// CONCEPT = shell voor relatie-onboarding (nog niet compleet). Standaard
// uitgefilterd in reguliere klantlijsten; na voltooien onboarding → ACTIEF.
export type ClientStatus = 'CONCEPT' | 'ACTIEF' | 'INACTIEF'
export type PaymentCondition = 'DAGEN_0' | 'DAGEN_8' | 'DAGEN_14' | 'DAGEN_30' | 'DAGEN_60' | 'DAGEN_90'

export const PAYMENT_CONDITION_LABELS: Record<PaymentCondition, string> = {
  DAGEN_0: '0 dagen',
  DAGEN_8: '8 dagen',
  DAGEN_14: '14 dagen',
  DAGEN_30: '30 dagen',
  DAGEN_60: '60 dagen',
  DAGEN_90: '90 dagen',
}

// Aantal kalenderdagen per betaalconditie - afgeleide vervaldatum als een
// factuur zelf nog geen `dueDate` heeft (factuurdatum + termijn).
export const PAYMENT_CONDITION_DAYS: Record<PaymentCondition, number> = {
  DAGEN_0: 0,
  DAGEN_8: 8,
  DAGEN_14: 14,
  DAGEN_30: 30,
  DAGEN_60: 60,
  DAGEN_90: 90,
}

export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  KLANT: 'Klant',
  PROSPECT: 'Prospect',
  LEVERANCIER: 'Leverancier',
  PARTNER: 'Partner',
  INTERN: 'Intern',
}

export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  CONCEPT: 'Concept',
  ACTIEF: 'Actief',
  INACTIEF: 'Inactief',
}

export type SlaLevel = 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM'

export const SLA_LEVEL_LABELS: Record<SlaLevel, string> = {
  BRONZE:   'Bronze',
  SILVER:   'Silver',
  GOLD:     'Gold',
  PLATINUM: 'Platinum',
}

export type ContactMomentType = 'TELEFOON' | 'EMAIL' | 'BEZOEK' | 'TEAMS' | 'WHATSAPP'
export type OpvolgingStatus   = 'OPEN' | 'VOLTOOID' | 'VERLOPEN'

export const CONTACT_MOMENT_TYPE_LABELS: Record<ContactMomentType, string> = {
  TELEFOON: 'Telefoon',
  EMAIL:    'E-mail',
  BEZOEK:   'Bezoek',
  TEAMS:    'Teams',
  WHATSAPP: 'WhatsApp',
}

export const OPVOLGING_STATUS_LABELS: Record<OpvolgingStatus, string> = {
  OPEN:     'Open',
  VOLTOOID: 'Voltooid',
  VERLOPEN: 'Verlopen',
}

// ─── Quotes ───────────────────────────────────────────────────────────────────

// Ronde 4 W3 (spec 03 §8) — primaire flow: CONCEPT → GEREED → VERSTUURD →
// GEACCEPTEERD → OMGEZET (of AFGEWEZEN). DEFINITIEF blijft beschikbaar voor
// backward-compat met legacy records.
export type QuoteStatus =
  | 'CONCEPT'
  | 'GEREED'
  | 'DEFINITIEF'
  | 'VERSTUURD'
  | 'GEACCEPTEERD'
  | 'AFGEWEZEN'
  | 'OMGEZET'
export type QuoteDisplayStatus = QuoteStatus | 'VERLOPEN'

export interface VatBreakdownRow {
  rate: number
  base: number
  vat: number
}

/** @deprecated Use VatBreakdownRow instead */
export type QuoteVatBreakdownRow = VatBreakdownRow

export type QuoteType = 'dienstverlening' | 'project' | 'onderhoud' | 'combinatie'

export interface ServiceInput {
  packageKey: string
  numberOfMonteurs: number
  hoursPerMonteur: number
  shiftType: string
  kmPerDay: number
  numberOfDays: number
  travelTimePerDay: number
}

export interface ProjectRoleInput {
  roleKey: string
  hours: number
}

export interface ProjectInput {
  roles: ProjectRoleInput[]
  // Features 19-22 (spec 20 §2.3.1) — overige (vaste) kostenregels zonder marge.
  // Backward compat: bestaande offertes zonder dit veld → behandel als [].
  otherCosts?: ProjectOtherCostInput[]
  // Features 19-22 (spec 20 §2.3.2) — per-offerte override van de risico-opslag
  // (in %). null/undefined = gebruik de globale `risico_opslag_pct`-parameter.
  riskSurchargeOverride?: number | null
}

// Features 19-22 (spec 20 §2.3.1) — overige projectkosten: vast bedrag, geen
// marge-berekening (in tegenstelling tot ProjectMaterialInput).
export interface ProjectOtherCostInput {
  description: string
  amount: number
}

export interface MaintenanceInput {
  normalHours: number
  overtime02Hours: number
  overtime24Hours: number
  saturdayHours: number
  sundayHours: number
}

// Ronde 4 W3 (spec 03 §4.3) — aparte travel-sectie naast Service/Project/
// Onderhoud. Wanneer travelEnabled actief is, mogen kmPerDay en
// travelTimePerDay in ServiceInput op 0 staan om dubbeltelling te voorkomen.
export interface TravelInput {
  kmPerDay: number
  numberOfDays: number
  travelTimePerDay: number
}

// Ronde 4 W3 (spec 03 §2.2) — prospect-gegevens op een offerte zonder
// gekoppelde Client. Wordt 1:1 opgeslagen in Quote.prospect*-kolommen.
export interface ProspectInput {
  companyName: string
  contactName: string
  email: string
  phone?: string | null
  street?: string | null
  houseNumber?: string | null
  postalCode?: string | null
  city?: string | null
  country?: string | null
  vatNumber?: string | null
  kvkNumber?: string | null
}

// Ronde 4 W3 (spec 03 §5.2) — per-offerte tarief-overrides. Key-formaat:
// "<category>.<key>", waarbij category overeenkomt met TariffMap-keys en
// key met TariffEntry.key. Value vervangt de basis-tarief-waarde tijdens
// calculatie van die ene offerte.
export type TariffOverrides = Record<string, number>

export interface ProjectMaterialInput {
  position: number
  description: string
  quantity: number
  purchasePrice: number
  marginPercentage: number
}

export interface TariffEntry {
  id: string
  category: string
  key: string
  label: string
  value: number
  unit: string | null
  sortOrder: number
}

export interface TariffMap {
  SERVICE_PACKAGE: TariffEntry[]
  SHIFT_SURCHARGE: TariffEntry[]
  PROJECT_ROLE: TariffEntry[]
  MAINTENANCE: TariffEntry[]
  TRAVEL: TariffEntry[]
  TAX: TariffEntry[]
}

// ─── ID validators (cuid + UUID v4) ──────────────────────────────────────────

export * from './id-validators'

// ─── E-mail (multi-recipient veld + splitter) ────────────────────────────────

export * from './email'

// ─── Werkaccount-activatie ───────────────────────────────────────────────────

export * from './werkaccount'

// ─── Offertes ────────────────────────────────────────────────────────────────

export * from './offertes'

// ─── Werkbonnen ──────────────────────────────────────────────────────────────

export * from './werkbonnen'

// ─── Werkbon-verzending (FO-111 + FO-112, batch-22 — ontvanger/cc + mailtekst) ─

export * from './werkbon-verzending'

// ─── Werktijden-afwijking — reden-dropdown + optionele toelichting ───────────

export * from './werkbon-uren-afwijking'

// ─── Facturen ────────────────────────────────────────────────────────────────

export * from './facturen'

// FO-011 (batch-13) — gedeelde factoring-/cessietekst + Finqle-IBAN-constanten.
export * from './facturen-factoring'

// FO-406 (batch-51) — facturatiemethode-registry (strategie per FacturatieVia).
export * from './facturatie-methodes'

// FO-406 (batch-51) — Finqle merchant-API: billing-request-/factuurstatussen,
// NL-labels en de gedeelde spiegel-DTO's.
export * from './finqle-api'

// FO-124 (batch-24) — BTW-aangiftefrequentie per BV (gedeeld type + UI-labels).
export * from './btw-frequentie'

// ─── Employee-mutaties (spec A — contract + salaris ingangsdatum) ────────────

export * from './employee-mutations'
// FO-044-P2 (batch-16) — gedeelde contracttype/einddatum-validatie (create + mutatie + forms).
export * from './employee-contract'
// FO-094 (batch-23) — contractbeheer-documenten met workflow + ondertekening.
export * from './employment-contracts'
// FO-388 (batch-46) — contract-verlengingswizard + ketenregeling + aanzegging.
export * from './contract-verlenging'
// FO-150 (batch-29) — CAO-voltijd-fallback (38u, Metaal en Techniek) voor de voltijdsnoemer-cascade.
export * from './cao'

// ─── Verlof ──────────────────────────────────────────────────────────────────

export * from './verlof'

// ─── Verlofprofiel (BIG5/BLOK 2) ─────────────────────────────────────────────

export * from './verlofprofiel'

// ─── Verzuim ─────────────────────────────────────────────────────────────────

export * from './verzuim'

// ─── Medewerkerstatus in twee lagen (FO-340, batch-40) ───────────────────────
// Gedeelde Zod-schemas + DTO's voor `dienstverband` (wat mag) en `werkstatus`
// (waar verschijn je). De pure sets/predicaten staan in @streep/utils.
export * from './medewerker-status'

// ─── HR-dossier (FO-086) ─────────────────────────────────────────────────────

export * from './dossier'

// ─── batch-41 dossier (FO-355/FO-358) — volledigheid + informatie-opvragen ───

export * from './dossier-volledigheid'

// ─── Selfservice (E2 mutatieverzoeken) ──────────────────────────────────────

export * from './selfservice'

// ─── Payroll (D1) ────────────────────────────────────────────────────────────

export * from './payroll'
// FO-324 P3 (batch-36) — doel-type-labels voor toeslag-regels (verloning vs facturatie).
export * from './toeslag'
// FO-319 (batch-35) — NMBRS-periode-import: kolomcontract, FO-318-categorieën,
// looncode-mapping + overzicht-DTO's.
export * from './nmbrs-export'
export * from './payroll-herleiding'
// FO-012 (batch-14) — reserveringsregister (periode-saldering + reservering N->N+1).
export * from './payroll-reservering'
// FO-305 P3 (batch-33) — payroll-uren-correctielaag (potjes + gedeeld Zod-schema).
export * from './payroll-uren-correctie'
// FO-013 (batch-14) — min-uren als afhandelbaar Postvak In-item (afboek-keuze + 2 weergaven).
export * from './min-uren'

// ─── Loonstroken (spec B — import + match + klaarzetten) ─────────────────────

export * from './loonstroken'

// ─── batch-59 loonstrook-controles (FO-433) ──────────────────────────────────

export * from './loonstrook-controles'

// ─── Uren / Weekstaat ────────────────────────────────────────────────────────

export * from './uren'

// ─── Declaraties ─────────────────────────────────────────────────────────────

export * from './declaraties'

// ─── Documenten ──────────────────────────────────────────────────────────────

export * from './documenten'

// ─── FO-378 (batch-44) Versiebeleid per documentsoort + copy-helper ──────────
export * from './document-versiebeleid'

// ─── Groene-kaart-extractie (voertuig-upload) — gedeeld response-contract ────
export * from './groene-kaart-extractie'

// ─── FO-328 (batch-38) Gedeelde upload-limieten client+server ────────────────

export * from './uploads'

// ─── FO-313 (batch-34) Federatieve bibliotheek-index ─────────────────────────

export * from './bibliotheek'

// ─── batch-39 bibliotheek (FO-333) — upload-metadata-voorstellen ─────────────

export * from './document-metadata-voorstel'

// ─── batch-43 bibliotheek (FO-375) — medewerker-koppeling-matcher ────────────

export * from './medewerker-match'

// ─── FO-036 Document-register + FO-035 HR-gespreksverslag (batch-16) ─────────

export * from './document-register'
export * from './gespreksverslag'
// batch-47 gespreksverslagen (spec 90) — event-gedreven statusverloop.
export * from './gespreksverslag-status'

// ─── Employee-documenten (spec 32) ───────────────────────────────────────────

export * from './employee-documents'

// ─── NHG werkgeversverklaring (lane O1) ──────────────────────────────────────

export * from './werkgeversverklaring'

// ─── Reisafstanden (D2) ───────────────────────────────────────────────────────

export * from './reisafstanden'

// ─── Reisuren (BLOK 5 — automatische reisuren-flow per werkbon-dag) ─────────

export * from './reisuren'

// ─── Planning ────────────────────────────────────────────────────────────────

export * from './planning'

// ─── Capaciteitsplanning (Ronde 4 W8) ────────────────────────────────────────

export * from './capacity'

// ─── Notificaties (E3) ────────────────────────────────────────────────────────

export * from './notificaties'
// FO-307-A5 (batch-33) - offboarding-spiegel (portal).
export * from './offboarding'

// ─── Notificatie & Signalering Engine (Ronde 4 W7) ────────────────────────────

export * from './notification-rules'

// ─── batch-56 notificatie-override (FO-416) - ontvangers per meldingssoort ───

export * from './notificatie-ontvangers'
// batch-67 meldingen-ontvangers (FO-500) - het herleidbaarheidsspoor van een
// melding (`Notificatie.ontvangerBron`) en de "Waarom"-zin eronder.
export * from './notificatie-bron'
export * from './notificatie-resolver-labels'
export * from './notificatie-resolver-soort'
export * from './notificatie-waarom'
export * from './postvak-rolpostvak'

// ─── QR-labels (labelpresets voor de QR-PDF-generator) ──────────────────────

export * from './qr-labels'

// ─── Voertuigen / Materieel (F3) ──────────────────────────────────────────────

export * from './voertuigen'
// FO-113 (batch-22) — verklaring zakelijk/privé-gebruik (auto) + bijtelling-koppeling.
export * from './voertuig-verklaring'
// FO-018 (batch-14) — tankpas leverancier-lijst + pincode (gedeeld schema + DTO's).
export * from './tankpas'
// FO-022 (batch-15) — tankpas-AI-inlezen (upload/match/bevestig request-schemas).
export * from './tankpas-ai'
// FO-038 (batch-16) — voorkeurstelefoon (Facilities) beheerbare keuzelijst.
export * from './voorkeur-telefoon'
// Spec 40 — bijtelling (splittarief EV + auto als bron): tarieventabel-beheer + regeling-DTO.
export * from './bijtelling'
// FO-361 (batch-41) — netto-bijtelling: belastingjaar-parameters + overzicht/simulatie-DTO's.
export * from './bijtelling-netto'

// ─── Kalender (F6) ───────────────────────────────────────────────────────────

export * from './kalender'

// ─── HRM (F1 — skill matrix + certificate dashboard) ─────────────────────────

export * from './hrm'

// ─── Communicatie (F7) ────────────────────────────────────────────────────────

export * from './communicatie'

// ─── Rapportage (F12) ─────────────────────────────────────────────────────────

export * from './rapportage'
// Batch-8 rapportage — reistijd-afwijking-signaal (actie-endpoints + drempels).
export * from './reistijd-signaal'

// ─── Onboarding (F2) ──────────────────────────────────────────────────────────

export * from './onboarding'
export * from './onboarding-skills'

// ─── Onboarding dashboard (Spec C §8) ─────────────────────────────────────────

export * from './onboarding-dashboard'
// batch-42 onboarding (FO-369) — blokkade-DTO + reden-labels + tekst-builder.
export * from './onboarding-blokkade'
// batch-42 onboarding (FO-370) — aanmeldpakket-export preview-DTO.
export * from './aanmeldpakket'

// ─── Onboarding-documenten (spec C §5 / §14.1 — PR C4 + PR C8) ───────────────

export * from './onboarding-documenten'
// batch-45 onboarding (FO-381) — flow-uitgangen (annuleren/verwijderen/geforceerd afronden).
export * from './onboarding-uitgangen'
export * from './onboarding-akkoord'
// batch-39 onboarding (FO-337) — Reglementen & akkoorden.
export * from './onboarding-reglementen'

// ─── Intercompany (H) ─────────────────────────────────────────────────────────

export * from './intercompany'
// ─── Inkoop (F9) ──────────────────────────────────────────────────────────────

export * from './inkoop'
export * from './inkoop-besteltraject'
export * from './inkoop-betaallijst'
// FO-147 (batch-28) — kosten-toewijzing: meerdere medewerkers per regel + org-trends.
export * from './inkoop-toewijzing'
// FO-320 (batch-36) — kostendragers: generiek doeltype (medewerker/voertuig/drager).
export * from './kostendrager'
export * from './leverancier-status'
export * from './leverancier-categorie'
// ─── Taken & Signalen (E4) ────────────────────────────────────────────────────

export * from './taken'
// ─── Operationele takenmodule (spec 34 — backlog, planbord, escalatie) ───────

export * from './operationele-taken'
// ─── Portal-actiepopup (openstaande acties bij het openen van het portaal) ────

export * from './portal-acties'
// ─── Impersonation / Medewerker-preview ──────────────────────────────────────

export * from './impersonation'
// ─── Diensten (F11 — interne dienstencatalogus) ──────────────────────────────

export * from './diensten'

// ─── Formulieren (F5 — dynamische form-builder + inzendingen) ────────────────

export * from './formulieren'

// ─── Projecten (F10 — projectoverzicht + budget + voortgang) ─────────────────

export * from './projecten'

// ─── Targeting (F8 — acquisitie/leads) ───────────────────────────────────────

export * from './targeting'

// ─── Acquisitie flow-spine (FO-102 — status-overgangen + guard) ──────────────

export * from './acquisitie'

// ─── Targeting map (Google Maps DTO's + Zod-schemas) ─────────────────────────

export * from './targeting-map'

// ─── Targeting dashboard-layout (spec 27) ────────────────────────────────────

export * from './targeting-dashboard-layout'

// ─── Goedkeuringen-inbox (E6) ─────────────────────────────────────────────────

export * from './goedkeuringen-inbox'

// ─── Kwaliteit (F4) ───────────────────────────────────────────────────────────

export * from './kwaliteit'
export * from './kwaliteit-templates'

// ─── Dashboard (F13 — backoffice admin-dashboard) ─────────────────────────────

export * from './dashboard'

// ─── FO-058 (batch-18) — onderhoudsschema-controle + geautomatiseerde mails ───
export * from './onderhoud-controle'
export * from './mail-automatisering'

// ─── Mail ─────────────────────────────────────────────────────────────────────

export * from './mail'
// FO-156 (batch-31, stream mail-tracking) — open-tracking (pixel).
export * from './mail-tracking'

// ─── Klantafspraken (Ronde 3 — PR-1, spec 01) ────────────────────────────────

export * from './agreement-rules'

// ─── CRM uitbreiding (PR-6, Ronde 3) ─────────────────────────────────────────

export * from './crm-uitbreiding'

// ─── Consignatie-rooster (Ronde 3 — PR-3, spec 01 §3) ────────────────────────

export * from './consignatie'
// FO-307-A2 (batch-33) - consignatie/piket ruilverzoek (portal + planner).
export * from './consignatie-ruil'

// ─── Exports (PR-7 — 8 Excel-exports met permissie + branding) ────────────────

export * from './exports'

// ─── Relatie-import (FO-092 batch-23 — gedeeld per-rij Zod-schema + report) ────

export * from './relatie-import'

// ─── Relatie-KVK (FO-395 batch-46 — periodieke KVK-mutatiescan) ──────────────

export * from './relatie-kvk'

// ─── Verantwoordelijkheden (FO-096 batch-21 — wie waarvoor verantwoordelijk) ──

export * from './verantwoordelijkheden'

// ─── Globale zoek (spec 19 §1 + §3 — Command Palette) ────────────────────────

export * from './search'

// ─── Gereedschap / Assets (spec 19 §9 — Gereedschappen-tab op /materieel) ─────

export * from './gereedschap'

// ─── Gereedschapspakketten (FO-041, batch-16 — tool-pakket-flow) ─────────────

export * from './gereedschapspakket'

// ─── Gereedschap set-/los-uitgifte + monitoring + portal (FO-136, batch-27) ──

export * from './gereedschap-uitgifte'

// ─── Leverwijze: uit voorraad of extern bestellen (FO-373, batch-42) ─────────

export * from './leverwijze'

// ─── AI Rapportage (PR-8, Ronde 3) ───────────────────────────────────────────

export * from './ai-rapportage'

// ─── Campagne (Grote Vakantie Campagne) ──────────────────────────────────────

export * from './campagne'

// ─── Gespreksaanvragen (PR-M, spec 01 §3) ────────────────────────────────────
// @deprecated — gebruik `contact-requests` (Spec C). Blijft beschikbaar voor
// backward-compatibility van bestaande routes/UI tijdens Track C-migratie.

export * from './gesprekken'

// ─── Contactverzoeken (Spec C — vervangt gespreksaanvragen) ─────────────────

export * from './contact-requests'

// ─── Ideeenbox / verbetervoorstellen (batch-10 #24) ──────────────────────────

export * from './ideeen'

// ─── HRM gespreksagenda (batch-6 hrm) ────────────────────────────────────────

export * from './hr-gesprekken'

// ─── Opleidingen / cursussen v2 (Spec D) ────────────────────────────────────

export * from './opleiding'
// FO-019 (batch-15) — cursisten aanmelden bij externe opleider (template + resolver).
export * from './opleiding-aanmelding'
// FO-380 (batch-44) — cursusdag levert altijd een werkbon(dag): aanvul-resultaat per deelnemer.
export * from './opleiding-werkbon-aanvullen'

// ─── Postvak In (Track-A — centraal inbox-systeem) ───────────────────────────

export * from './postvak-in'

// ─── Voorraad / Magazijn (PR-C, spec docs/specs/voorraad-magazijn-module.md) ─

export * from './voorraad'
// batch-69 magazijn-cijfers (FO-521/FO-520) — onderdeel-filter + voorraadwaarde.
export * from './voorraad-waarde'
// batch-69 magazijn-cijfers (FO-520) — telsessie, correcties en audittrail.
export * from './voorraad-telling'

// ─── Artikel-beoordeling (batch-10 #25 — medewerker beoordeelt uitgereikte spullen) ─

export * from './artikel-beoordeling'

// ─── Batch-16 employee-kosten (FO-033 + FO-048) ──────────────────────────────
// Generieke beoordeling + boetes + kosten-actuals/bestellingen op de medewerker.
export * from './beoordeling'
export * from './boete'
export * from './employee-kosten'

// ─── Bestandsnaam-patronen (batch-11 F3 — instelbaar per documenttype) ───────

export * from './bestandsnaam-patronen'

// ─── Client-mailnotificaties (batch-11 KL9 — per klant + per event) ──────────

export * from './client-email-notificaties'

// ─── Relatie-aantekeningen (FO-095, batch-21) ───────────────────────────────

export * from './client-note'

// ─── Contractbeheer relaties (FO-093, batch-21) ─────────────────────────────

export * from './client-contract'

// ─── Overeenkomsten (FO-139, batch-27) ──────────────────────────────────────

export * from './overeenkomsten'

// ─── Materieel-abonnementen (FO-128, batch-24) ──────────────────────────────

export * from './abonnement'

// ─── Interne STREEP-beoordeling (batch-11 KL2 — los van klant-beoordeling) ───

export * from './interne-beoordeling'

// ─── Validatie-fout-normalisatie (issues[]/details → per-veld) ───────────────
export * from './validation-issues'

// ─── Certificaattypes (batch-6 voertuig §3) ──────────────────────────────────
export * from './certificate-types'

// ─── Certificaat-aanvragen (spec 37 — medewerker dient in, backoffice keurt goed) ─
export * from './certificaat-aanvragen'
// FO-046-P2 (batch-16) — portal document-upload met TL/HR-goedkeuring.
export * from './medewerker-document-aanvragen'

// ─── Voorraad — unieke items + QR + autosleutel (spec 30) ────────────────────

export * from './voorraad-uniek'

// ─── Karren & koffers — dragers/lades/modules/templates/controle (FO-310) ────
export * from './karren'

// ─── Servicekoffer — SK-reeks, inhoud (pakket + afwijkingen), uitrol (FO-301) ─

export * from './koffer'

// ─── Materieel-bestelling — order-groepering per leverancier (FO-059) ─────────

export * from './materieel-bestelling'

// ─── Onboarding-uitbreiding (Spec C) — Kandidaten-pipeline ───────────────────

export * from './kandidaten'

// ─── Spec C §3 — 4-ogencontrole reviews ──────────────────────────────────────
export * from './onboarding-reviews'

// ─── Spec C §6 — Onboarding-asset-reserveringen ──────────────────────────────
export * from './onboarding-assets'

// ─── FO-003 (batch-12) — Onboarding-startworkflow + verplichte-certificatenlijst
export * from './onboarding-startworkflow'

// ─── Spec B — Bellijsten & contacten DTOs ────────────────────────────────────
export * from './bellijst'

// ─── Portal "Hulp vragen aan collega" (klant-historie + vraagstuk-match) ──────
export * from './hulp'

// ─── Verjaardag-keuze (PR #169) ──────────────────────────────────────────────
export * from './verjaardag-keuze'

// ─── Medewerker-app DTOs (PR #174) ───────────────────────────────────────────
export * from './medewerker-app'

// ─── Ronde 4 W5 — Trends & Analyse module ────────────────────────────────────
export * from './trends'

// ─── Features 19-22 — calculatie / autorisatie / dashboard ───────────────────
// Conform docs/specs/20-, 21-, 22-*. Schema + types + utils + seeds (PR #A);
// UI/API in PR #C/#D/#E.
export * from './calculation-parameters'
// Batch-9-vervolg V1 (PO #4) — normuren/werkdagen per jaar/BV/functie/regeling.
export * from './normuren'
export * from './employee-cost'
// Batch-4 calc — Calculatie & Kostprijs-module (calculatie-domein, engine-I/O,
// DTO's + Zod). De pure rekenlogica leeft in @streep/utils/calculation-engine.
export * from './calculation'
// Batch-9 #71 — kostprijs ↔ opbrengst vergelijk-weergave (klant/rooster,
// per uur/week/maand, marge-tool). Pure logica in @streep/utils/kostprijs-vergelijk.
export * from './kostprijs-vergelijk'
export * from './authorization'
export * from './workspace'
export * from './user-dashboard'
// FO-153 (batch-32) — per-gebruiker module-tab-volgorde: request-schema +
// strip-registry (geldige tab-keys per werkruimte-sleutel).
export * from './nav-voorkeuren'
// Spec 20 §2.5 + §2.6 — management-dashboard DTO's (PR #C).
export * from './management'

// ─── S6 — Klant-documenttemplates (opdrachtovereenkomst / opdrachtbevestiging) ─
export * from './client-document-templates'

// ─── FO-410 (batch-54) — DE canonieke placeholder-key + variant-dedupe ────────
// Eén normalisatie voor render-resolver, DOCX-scanner, wizard en config-editor.
export * from './placeholder-canonicalisatie'

// ─── Docgen-uitbreiding — PlaceholderConfig, scanner, category ────────────────
export * from './document-templates'

// ─── FO-312 — Getypeerde placeholder-registry (frontend-rendering + API-validatie) ─
export * from './document-placeholders'

// ─── Relatie-onboarding (klantvragenlijst) ───────────────────────────────────
export * from './client-onboarding'
// ─── batch-61 klant-onboarding-docgen (FO-450) — onboarding in het klantportaal ─
export * from './klant-onboarding-portaal'

// ─── Profiel-generator (medewerker-profiel .pptx + AI-concept) ───────────────
export * from './profiel'

// ─── Veiligheid (LMRA/TRA/LOTOTO checklists, werkinstructies, medewerker-documentatie) ─
export * from './veiligheid'

// ─── Huisstijl/briefpapier (Word/Excel op STREEP-huisstijl + PDF) ────────────
export * from './huisstijl'

// ─── Meldformulier onveilige situatie (Veiligheid-module — eigen model) ───────
export * from './onveilige-situatie'

// ─── Voertuigwijziging-melding (FO-116-B, batch-22 — medewerker-app) ──────────
export * from './voertuig-melding'

// ─── Kilometerstand-registratie (FO-157, batch-31 — km-herinnering + invoer) ──
export * from './voertuig-km'

// ─── Voertuig-onderhoudsrapport (FO-327, batch-37 — telematica-ingest) ───────
export * from './voertuig-onderhoudsrapport'

// ─── Garagefacturen → onderhoudshistorie + backfill (FO-329, batch-38) ───────
export * from './garage-onderhoud'

// ─── Schademelding / Europees aanrijdingsformulier (FO-326, batch-37) ─────────
export * from './schade-melding'

// ─── Uitrusting (credit-gestuurde kleding/gereedschap/PBM-bestellingen) ──────
export * from './uitrusting'

// ─── Ongevallen-/incidentenregistratie ────────────────────────────────────────
export * from './ongevallen'

// ─── Vertrouwenspersoon-module (Spec 35) ─────────────────────────────────────
export * from './vertrouwenspersoon'

// ─── Medewerker-notities / dossiernotities (Spec 35) ─────────────────────────
export * from './medewerker-notities'

// ─── Dienst defaults (shared between API and backoffice) ──────────────────────
import type { PloegSoort } from './werkbonnen'

export const DEFAULT_DIENST_TIJDEN: Record<PloegSoort, { start: string; eind: string }> = {
  DAG:   { start: '08:00', eind: '17:00' },
  VROEG: { start: '06:00', eind: '14:15' },
  LAAT:  { start: '14:15', eind: '23:00' },
  NACHT: { start: '21:00', eind: '06:00' },
}

export const DEFAULT_DIENST_PAUZES: Record<PloegSoort, number> = {
  DAG:   30,
  VROEG: 30,
  LAAT:  30,
  NACHT: 30,
}
export * from './kandidaat-aanbiedingen'

// ─── Klantenportaal batch-10 (#14 veiligheid-inzendingen) ────────────────────
export * from './klant-veiligheid'

// ─── Klantenportaal batch-10 (#12 periodieke beoordeling) ────────────────────
export * from './klant-beoordeling'

// ─── FO-033 — Ops-rapportage-instellingen (gedeeld settings-schema) ───────────
export * from './ops-rapportage'
export * from './ops-dashboard'

// ─── FO-144 (batch-28) — RI&E (risico-inventarisatie en -evaluatie) ──────────
export * from './rie'

// ─── FO-149 (batch-29) — uitrusting: beschikbaarheid per artikel (doelgroepen) ─
export * from './uitrusting-doelgroep'

// ─── Verwerk-stap uitrustingbestellingen (2026-08) ───────────────────────────
export * from './uitrusting-verwerking'

// ─── FO-321 (batch-36) — gereedschap-catalogus vul-wizard (voorraad-koppeling) ─
export * from './catalogus-wizard'

// ─── FO-150 (batch-30) — opleiding: klant informeren bij cursus-conflict ─────
export * from './opleiding-klant-mail'

// ─── FO-302 (batch-32) — generiek: klant informeren bij afwezigheid ──────────
export * from './klant-afwezigheid-mail'

// ─── FO-306 (batch-33) — klantenportaal skillmatrix: monteur samenstellen ────
export * from './monteur-profiel'
// ─── FO-304 + FO-308 (batch-33) — klantenportaal-uitbreiding schrijf-schemas ─
export * from './klant-portaal'

// ─── FO-322 (batch-36) — AVG-toestemmingen (register + afdwinging) ───────────
export * from './toestemmingen'

// ─── FO-323 (batch-36) — NEN 3140-aanwijzingen (VOP/VP/WV/IV) ────────────────
export * from './nen3140'

// ─── FO-335 (batch-39) — uitgegeven middelen als kostenbron + backfill ───────
export * from './uitgifte-kosten'
export * from './mail-quarantaine'

// ─── FO-391 (batch-46) — jaarlijkse tarief-indexatie (preview/versioneren/terugdraaien) ───
export * from './indexatie'

// ─── FO-399 (batch-49 acquisitie-threads) — gedeelde regels voor het vervolg-contactmoment ───
export * from './contactmoment-draad'

// ─── FO-404 (batch-50 accountant-rol) — vergrendelde ACCOUNTANT-allowlist ────
export * from './accountant'

// ─── FO-412 (batch-54 klant-akkoord-mail) — klant-akkoord per mail-reply ────
export * from './klant-akkoord-mail'

// ─── Verkoopfacturen-mailbox (verkoopfactuur@streep.nl) — 7e mail-ingest ────
export * from './verkoop-mail'

// ─── FO-417 — missende nummers in de verkoopfactuurreeks per BV ─────────────
export * from './factuurreeks-gaten'
// ─── batch-63 facturen-bronregel (FO-470) — facturatieverzoek als bron, koppelaar,
// nummer-stand in de facturenlijst en dedupe bij binnenkomst ────────────────
export * from './facturen-bronregel'

// ─── FO-419 (batch-55 facturen-intercompany) — intercompany-facturen + patronen ─
export * from './intercompany-facturen'
// ─── FO-420 (batch-55 inkoop-correctiemail) — correctie-mail bij oude BV-naam + per-BV bijlage ───
export * from './inkoop-correctie-mail'
// ─── FO-421 (batch-55 bruikleen-docs) — bruikleendocumenten medewerker + item ─
export * from './bruikleen-documenten'

// ─── FO-413 (batch-56 klantenportaal) — verbeterronde D1-D8 (tour, controlestap,
// zichtbaarheid, ervaringscategorie, gereedschap, gewenst profiel, monteur-vrijgave) ─
export * from './klantenportaal-verbeterronde'

// ─── FO-415 (batch-57 overeenkomst-vervolg) — vervolgblok na genereren +
// automatische bruikleenovereenkomst bij middel-wisselingen ──────────────────
export * from './overeenkomst-vervolg'

// batch-58 uitdienst-verlof (FO-424) - de verlof-stap in de uitdienst-flow:
// bevestigings-plan (intrekken/inkorten/afwijzen per aanvraag), de cockpit-stap
// met tellingen en de onnodig geworden correcties bij een latere einddatum.
export * from './uitdienst-verlof'
// ─── FO-429 (batch-58 profiel-match) - zachte profielwens-hint in het
// toewijzingspad: score + criteria per monteur, plus de query van het endpoint ─
export * from './profiel-match'
// ─── batch-58 klant-portaal-beheer (FO-427 + FO-428 + FO-426 P6/P8) — één
// vrijgave-begrip voor overeenkomsten/documenten, uitrol-status + activatie-
// checklist, per-pagina-rechten op het klant-account en de afspraken-pagina ───
export * from './klantportaal-beheer'

// ─── batch-59 voertuig-afspraken (FO-435 + FO-436) — het afspraak-begrip op het
// voertuig (plannen/monitoren/afronden), de interne afhandel-registratie van een
// RDW-terugroepactie en de garage-historie-import ────────────────────────────
export * from './voertuig-afspraken'
// ─── batch-59 interne-facturen (FO-432) - interne (intercompany) INKOOPfacturen:
// patroonherkenning per BV-paar, herhaal-regels die per periode een concept
// KLAARGEZET zetten (boeken pas na bevestiging) en de samenvoeg-flow met het
// mail-exemplaar ─────────────────────────────────────────────────────────────
export * from './interne-facturen'
// ─── FO-434 (batch-59 werkbon-bundel-finqle) - werkbonnen als één zending naar
// het Finqle-parse-adres: groepering per BV x klant, uitvallers met reden,
// bijlage-modus en de mailgrootte-splitsing in delen ───────────────────────────
export * from './werkbon-bundel-finqle'
// ─── batch-60 vaste-monteur (FO-440) - vaste monteur per werkbon-slot, de
// bezettingsstatus (VAST/VERVANGEN/NOG_TE_PLANNEN/conflict) en de
// vervanging-monitor-DTO's; de pure resolver zelf staat in @streep/utils ────
export * from './vaste-monteur'
// ─── batch-60 klantportaal-zichtbaar-verzoeken (FO-439b K4) — uniforme klantverzoek-opvolging ──
export * from './klantverzoeken'
// ─── batch-60 facturen-verwijderen (FO-443) - origin-bewuste verwijderbaarheid,
// verwijder-record, genegeerd-marker (sync-bestendig) en herstel/bulk ─────────
export * from './facturen-verwijderen'
// ─── batch-61 portal-collega (FO-446) - collega op dienst in de portal-planning:
// de per-dagregel `collegas[]`-DTO (naam/functie/tijden/status/opt-in) ────────
export * from './collega-op-dienst'

// ─── batch-61 werkbon-gefactureerd (FO-449) - facturatie-spoor op de werkbon naast
// de operationele status: DTO, labels, lijst-filter en de gedeelde request-schema's
// (markeren met reden / terugdraaien / bulk) ──────────────────────────────────
export * from './werkbon-facturatie'

// ─── batch-61 welkomstmail (FO-448) - "Welkomstmail + inlog delen" voor
// klantportaal-gebruikers (eenmalige inloglink, NL/EN) en medewerkers (Entra) ──
export * from './welkomstmail'

// ─── batch-61 uitrusting-credits-maten (FO-445 + FO-455) - Van Ysselt-saldo-
// berichten (wachtrij/dagbundel), afstemming + correctie, maandoverzicht,
// regel-retour en de maten-leesweergave (MedewerkerMatenDTO) ─────────────────
export * from './uitrusting-credits-sync'

// ─── batch-62 uitrusting-inkoop (FO-466 + FO-467) - pakbon/factuur ->
// medewerker-uitrusting met dubbel-controle, en verwerken zonder bestelling
// (telverschillen, dubbele aanvragen, opschoonlijst).
export * from './uitrusting-inkoop'
// ─── batch-62 garage-voertuig (FO-462 + FO-465) - onderhoud vs schade per
// garagefactuurregel, schade aan berijder + dossier, inzicht per voertuig/
// medewerker, afspraak-keten met documenten, reminders met interval per soort
// en de garagefactuur-review ─────────────────────────────────────────────────
export * from './garage-voertuig'
// ─── batch-62 kostprijs-werkelijk (FO-460 + FO-461) - brandstoffactuur-regels +
// koppeling, versie-vaste loonmapping, bijtelling-toerekening en de werkelijke
// kostprijs naast de norm ───────────────────────────────────────────────────────
export * from './kostprijs-werkelijk'
// ─── batch-62 telefoonabonnementen (FO-463) - telefonie-register op Abonnement
// (TELEFOON/DATA): register-overzicht, bulkacties, nummerportering, providerlijst,
// instellingen, providerfactuur-specificatie, controle-werkvoorraad en import-rapport ──
export * from './telefonie'
// FO-508 (spec 208) — telefoon en simkaart als set, uitzonderingsaanvraag.
export * from './middel-uitzondering'
// ─── batch-62 briefpapier (FO-459) - briefpapier-bibliotheek met varianten,
// keuze bij generatie (voorstel/toepassing), standaard per template/soort,
// factuur-briefpapier (render-instellingen + uitzondering) en de documentweergave ──
export * from './briefpapier'
// ─── batch-62 klant-vaste-inzet (FO-458) - de vaste inzet-kring van een klant
// (rol/gronden/status), het uitzondering-schema voor het beheer en de klant-
// vormen voor afwezige perioden en dienst-gaten ─────────────────────────────────
export * from './klant-vaste-inzet'
// ─── batch-63 pauze-tweeassig (FO-473) - de pauze op twee assen (verloning en
// facturatie): herkomst per as, schrijf-schema's per dienst/dagregel en de DTO's
// voor het klantoverzicht "pauze-instellingen" en de facturatie-specificatie ──
export * from './pauze-tweeassig'
// ─── batch-63 certificaat-mailbox (FO-469) - certificaten automatisch inlezen uit
// de certificaat-mailbox: ingest-/itemstatussen, review-redenen, AI-extractie-
// schema (array per bundel, zekerheid per veld), match-onderbouwing, review-
// acties, synoniemen en de mailbox-instellingen ─────────────────────────────────
export * from './certificaat-mailbox'
// batch-63 cursusplanning (FO-468) - cursusaanvraag aan de opleider: statussen,
// mailteksten (bovenop de FO-019-template), Zod-schema's en DTO's.
export * from './cursus-aanvraag'
// batch-65 inkoop-kosten (FO-482/FO-483) - kostensoorten-stamtabel met hoofdgroepen
// (DTO's, gedeelde schema's, keuzelijst-helpers, detail-mappings) en de startvulling.
export * from './kostensoorten'
export * from './kostensoorten-seed'
// batch-65 inkoop-kosten (FO-483) - toerekenen aan klant/werkbon/project en
// doorbelasten naar de verkoopfactuur (statussen, schema's, DTO's, werkvoorraad).
export * from './inkoop-doorbelasten'
// batch-65 inkoop-kosten (FO-483) - kosten en marge per klant, inkoopkosten per werkbon/project.
export * from './inkoop-kosten-inzicht'
// batch-66 inkoop-afwijkingen (FO-490) - afwijkingen-register op inkoopfacturen:
// soorten, statussen, uitkomsten, gedeelde Zod-schema's (registreren, status,
// melden, creditnota koppelen, instellingen) en de DTO's van paneel en werkvoorraad.
export * from './inkoop-afwijkingen'
// batch-65 voorraad-besteladvies (FO-481) - gebundeld besteladvies per
// leverancier: statussen, DTO's en de gedeelde Zod-schema's (API + formulieren).
export * from './besteladvies'
// batch-65 finqle-verzamelbon (FO-484) - samengevoegde werkbonnen als één
// verzamelbon naar Finqle: bundelperiode per klant, statussen/labels, DTO's
// (backoffice, lijst-/planbordreferentie, klantportaal zonder bedragen), de
// klantbeoordeling van een bundel en de losse-verzending-waarschuwing.
export * from './verzamelbon'
// batch-65 uitrusting-flow (FO-480) - lever-/afhaalafspraak: statussen, DTO's
// (portal, backoffice, planbord) en de gedeelde mutatie-schemas.
export * from './uitrusting-levering'
// batch-65 apk-rapport (FO-478) - APK-rapport als documenttype: gedeelde velden-
// en invoer-schema's (form + API), uitslag, AI-voorinvulling, voertuig-keuze,
// registratie-uitkomst, waarschuwingen en de hertypeer-lijst.
export * from './apk-rapport'
// batch-65 factuur-richting (FO-486) - verkoopconcept boeken als inkoopfactuur
// en terug: richting-samenvatting, beschikbaarheid + uitleg per knop, voorstel/
// resultaat-DTO's, gedeelde Zod-schema's en de instellingen (Finqle-kenmerken,
// automatisch boeken als inkoop, default uit).
export * from './factuur-richting'
// batch-66 query-boolean (FO-488) - `queryBoolean()`: boolean uit een query-string
// zonder de `z.coerce.boolean()`-val (de string "false" werd daar `true`).
export * from './zod-query'
export * from './zod-patch'
// batch-66 voertuigcontrole (FO-489) - zichtbare gezondheid van de RDW-sync, de
// twee tellers (inkoop-kenteken zonder voertuigvoorstel, loonstrook GEEN) en de
// kentekenherkenning per inkoopfactuurregel.
export * from './voertuigcontrole'
// batch-66 interne-geldstroom (FO-494) - inkoopfactuur, spiegel-verkoopfactuur en
// engine-doorbelasting verbonden in één interne geldstroom: statussen, matcher-
// uitkomsten, de gedeelde request-schema's en de DTO's (voorstel, bulk, overzicht).
export * from './interne-geldstroom'
// batch-66 verkoop-extractie (FO-493) - opmaakprofielen voor de factuurextractie:
// velden + zekerheid per veld, gestructureerde regels, profiel-DTO's, de werkbon-
// en verzoekkoppeling, de leerlus (correcties, voorstellen), de instellingen
// (factoring-IBAN's, leerdrempel), het kwaliteitsdashboard en opnieuw uitlezen.
export * from './factuur-extractie'
// batch-66 contract-mijlpalen (FO-496) - de mijlpaal-workflow rond het
// arbeidscontract: codes/ankers/doelgroepen, de geseede standaardreeks M1 t/m M9,
// de proeftijdregel, formulier- en request-schema's en de DTO's van het
// contractdossier, het HR-overzicht Contractbewaking en de portal-gesprekken.
export * from './contract-mijlpalen'
// batch-66 kluis-niveaus (FO-495) - beveiligingsniveaus, toegangslijst (rol/
// persoon/afdeling, tijdelijk), eigenaar, onthulvereisten per niveau, de
// 4-ogen-vrijgave en de herbevestiging: enums, labels, gedeelde Zod-schema's en DTO's.
export * from './kluis'
// batch-67 portal-thema (FO-504) - feestelijke thema's op het portal-dashboard:
// de zes soorten, labels en prioriteit, het gedeelde instellingen-schema met
// expliciete defaults en het `thema`-DTO op de dashboard-identiteitsfetch.
export * from './portal-thema'
// batch-67 hr-brieven (FO-506) - de HR-besluitworkflows (verlenging, omzetting,
// opzegging, non-actief, VSO, ontslag op staande voet, acceptatie opzegging):
// enums, labels, de verplichte brieftags, gedeelde Zod-schema's en DTO's.
export * from './hr-besluiten'

// batch-67 middelen-inname (FO-499 + FO-505) - middelen-ledger, uitgifte-/innameformulier,
// overdracht, instellingen (afschrijvingstabel innamewaarde, reparatie-goedkeurders).
export * from './middelen'
// batch-67 middelen-inname, fase B (FO-505) - reparatieverzoek met statusreeks,
// goedkeuring, verhaalbesluit (4-ogen), verrekenroute en bezwaar.
export * from './reparatie'
// batch-67 campagnes (FO-501) - bijtellingskeuze-campagne: keuzetypes, de
// grijs-/geel-kentekenregel, gedeelde request-schema's en de portal-/
// backoffice-DTO's (beoordeling, opvolging, tellers per BV).
export * from './campagne-bijtelling'
// batch-67 campagnes (FO-503) - evenement-campagne (kerstborrel): thema, introducees
// (relatie, kind-leeftijd), de wizard- en beheer-schema's, tellers per
// leeftijdsgroep en de portal-/organisatie-DTO's.
export * from './campagne-evenement'
// ─── batch-68 controle (FO-342 + FO-343) ─────────────────────────────────────
// Generieke controlekern: enums, regel-registry, request-schema's en DTO's van de
// verloningscontrole (en straks de facturatiecontrole).
export * from './controle'
// batch-68 controle (FO-343 §4.2) - reistijd-maxima + activering op de factuur.
export * from './reistijd-instelling'
// batch-68 werkplaatsfacturen (FO-341) - afzenderprofielen, trefwoordenlijst per
// garage, interval per merk/model/voertuig, terugdraaien van een intervalreset,
// de onderhoudsbadge (voertuig, lijst, portal) en de herkenning/vaste termijn in
// de inkoop-review.
export * from './werkplaatsfacturen'
// batch-68 handtekening (FO-345) - opgeslagen handtekening/paraaf, ondertekenaars
// per rol en volgorde, bevestigingsniveau per documentsoort, de foutcodes van het
// ondertekenen met hun melding (D1), gedeelde Zod-schema's en DTO's.
export * from './ondertekening'
// batch-68 gespreksvoorbereiding (FO-346) - het feitelijke dossierbeeld bij een
// HR-gesprek (acht rubrieken, feitenstrook, drempels, vastlegging), de
// ontwikkeldoelen/doorgroeiwens en de toolboxsessies in de veiligheidsengine.
export * from './gespreksvoorbereiding'
export * from './ontwikkeldoelen'
export * from './toolbox'
// batch-68 atv-tvt-uitleg (FO-344) - ATV en TVT in gewone taal: de woordenlijst
// (WERKTIJD_SPAREN_LABELS), de spaarkeuzes, de uitleg-DTO's voor portal en
// backoffice (`GET /me/werktijd-sparen`, veld `uitleg` op het verlofprofiel) en
// het legacy-rapport "TVT aan zonder ATV" met de opschoon-request.
export * from './werktijd-sparen'
// FO-517 (spec 217) - gestructureerde sollicitatiebeoordeling: vacatures, eisen,
// beoordelingsformulier, adviezen/second review, functiematrix en dossier-DTO's.
export * from './recruitment-beoordeling'
// Spec 218 - ouderschapsverlof boeken: losse dagen + weekpatroon + preview per dag;
// kind-emmers voor de omboeking bij HR-classificatie.
export * from './ouderschap-boeken'
// batch-69 bestelflow (FO-512, spec 212) - de delta's op de uitrusting-bestelflow:
// claim op de verwerking, correctie-naar-nul bij extern-met-voorraad, de tijdelijke
// route "voorraadtelling in opbouw", het verplichte minimumniveau en de status-tijdlijn.
export * from './uitrusting-bestelflow'
// batch-69 km-balans (FO-515, spec 215) - werk- en privekilometerbalans per
// medewerker per maand: bron (gemeten/geschat), zekerheid, verbruiksfactoren,
// instellingen, gedeelde Zod-schema's en de DTO's (portal zonder bedragen).
export * from './km-balans'
// batch-69 planbord-afspraak (FO-523, spec 223) - afspraak met een klant vanaf het
// planbord: gedeelde request-schema's (plannen, verplaatsen, afzeggen, verslag,
// zichtbaarheid), labels, de doelgroep CRM-vertrouwelijk en de DTO's.
export * from './planning-afspraak'
// batch-69 samenstellen (FO-509 / FO-510 / FO-516) - de samenstel-opdracht met de
// scan-afvinklijst: request-schema's (API + formulieren) en DTO's.
export * from './samenstelling'
// batch-69 samenstellen, fase C (FO-516) - de inname-afvinklijst (innamelijst los
// van de offboarding, direct inboeken, HR-besluit per niet-afgevinkt item).
export * from './inname-sessie'
// batch-69 werkbon-pauze (FO-511) - de klantpauze: verzend-gate, bevestigingslijst en
// verzoeken met pauze-twijfel.
export * from './klantpauze'
// batch-69 actiepunten (FO-514) - het register "Actiepunten app": enums, rechten,
// het seed-schema per batch, de mutatieschema's en de DTO's.
export * from './actiepunten'
// batch-69 tvt-saldi (FO-522 + FO-513) - TVT in de afboekvolgorde, het expliciete
// saldo-tekort, saldo verplaatsen, herverdelen incl. TVT, saldi in de min en
// rechttrekken met TVT (gedeelde schema's + DTO's).
export * from './verlof-saldi'
// batch-70 vakantiecampagne (FO-525 §B6-§B7) - het jaaroverzicht verlof per potje
// (start, erbij, opgenomen, correcties, eind) met de reconciliatie op het ledger.
export * from './verlof-jaaroverzicht'
// batch-70 vakantiecampagne (FO-525) - Vakantie 2027 als een aanvraag: spelregels,
// advies-tekstset, wensen (periode, losse en halve dag), indienen, collectief beoordelen.
export * from './vakantie-aanvraag'
// batch-70 uitrusting-controle (FO-531) - de campagnesoort UITRUSTINGSCONTROLE:
// setsoorten, controle per set en onderdeel, afhandeling en overeenkomsten.
export * from './uitrustingscontrole'
// batch-70 voertuig-meldingen (FO-527) - voertuigmutatie-events en de meldingen
// aan Wassink, Cartracker en de salarisverwerker (enums, DTO's, schema's).
export * from './voertuig-meldingen'
// batch-70 wagenpark-week (FO-528 + FO-529) - wekelijkse km-standen, trends, contract-km,
// rit-versus-uren-afwijkingen, tankpas-houderhistorie, tankpas-weekcijfers en het weekrapport.
export * from './wagenpark-week'
// batch-70 app-dashboard (FO-526) - Systeem > App dashboard: wijzigingen per FO,
// livegang, deployregistratie (contract van POST /systeem/deploys) en het
// bronnenpakket van sync en backfill.
export * from './app-dashboard'
// Materieel > Garantie - los garantieregister op serienummer (schema's, status, DTO).
export * from './garantie'
